import dayjs from 'dayjs';
import {useAtom, useAtomValue} from 'jotai';
import Lottie from 'lottie-react';
import {useEffect, useMemo, useRef, useState} from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {FULL_DATE_FORMAT} from '@/constants';
import {useScrollDepth} from '@/hooks/scrollDepth';
import {useGA4WithParams} from '@/hooks/tracking';
import {AiVoteStateType, AnalysisItem, BasicAnalysisResult} from '@/map/@types';
import {useFetchAiPrompt, useQueryRegionByPoint} from '@/map/api';
import {aiVoteListAtom, mobileBottomSheetExpandedAtom} from '@/map/atom';
import {AREA_OVERLAY_TYPE_TEXT} from '@/map/constants';
// 데스크톱 AI 해설과 동일한 문구/스타일/동작 재사용
import SkeltonLottie from '@/map/components/AiPrompt/assets/skelton.json';
import {ReactComponent as RefreshCircleIcon} from '@/map/components/AiPrompt/assets/refresh.svg';
import {Infomation} from '@/map/components/AiPrompt/Infomation';
import {PanelFooter} from '@/map/components/AiPrompt/PanelFooter';
import {AiSubHeader} from '@/map/components/AiPrompt/SubHeader';
import {AreaOverlayType, GeoDataType} from '@/map/enums';
import {distanceToString, getSidoShortName} from '@/map/utils';
import {ActionGuideTooltip} from '@/components/Tooltip';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {copyText} from '@/utils';
import {SHEET_SCROLL_BODY_ID} from '../../../BottomSheet';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
  data?: BasicAnalysisResult;
  isPending?: boolean;
  error?: string;
}

const CONTENT_ID = 'myfc-mobile-ai-prompt-content-body';

const getAreaTitle = (item: AnalysisItem) => {
  if (item[AreaOverlayType.CIRCLE]) {
    return `원형 · 반경 ${distanceToString(item.circle.radius ?? 0, {
      roundingMode: 'ceil',
    })}`;
  }

  return '';
};

export const AiCommentReport = ({item, data, isPending, error}: IProps) => {
  const id = item.id ?? '';
  const [aiVoteList, setAiVoteList] = useAtom(aiVoteListAtom);
  const voteState = aiVoteList[id];

  const {
    data: aiData,
    isPending: isAiPending,
    isError: isAiError,
    dataUpdatedAt,
    refetch,
  } = useFetchAiPrompt(id, data);

  const isError = !!error?.length || isAiError;

  const center =
    item.type === AreaOverlayType.CIRCLE ? item.circle?.center : undefined;

  const {data: region} = useQueryRegionByPoint(
    center
      ? {
          lng: center.x,
          lat: center.y,
          type: GeoDataType.Emd,
        }
      : undefined,
  );

  const areaName = useMemo(() => {
    return region?.name?.length ? getSidoShortName(region.name) : '분석 지역';
  }, [region?.name]);

  const timer = useRef<NodeJS.Timer>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isCopied, setCopied] = useState<boolean>(false);

  // 시트가 접혀 있으면 본문에 overflow가 없어 진행률을 잴 수 없다
  const isSheetExpanded = useAtomValue(mobileBottomSheetExpandedAtom);

  const analysisType = AREA_OVERLAY_TYPE_TEXT[item.type];
  const content = aiData?.result;

  const tracking = useGA4WithParams();

  // 반경·검색어가 바뀌면 id가 바뀌고 해설도 새로 생성되므로 진입으로 다시 집계한다.
  // (컴포넌트는 그대로 마운트돼 있어 mount 1회로는 스크롤 이벤트와 짝이 맞지 않는다)
  useEffect(() => {
    tracking('AI_analysis_open', {analysis_type: analysisType, area: areaName});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 시트 본문은 탭·반경이 바뀌어도 스크롤 위치를 유지한다. 새 해설은 처음부터 보여주고,
  // 읽지 않은 구간이 진행률로 잡히지 않도록 맨 위로 되돌린다.
  useEffect(() => {
    const container = document.getElementById(SHEET_SCROLL_BODY_ID);

    if (container) {
      container.scrollTop = 0;
    }
  }, [id]);

  // 해설을 어디까지 읽었는지(스크롤 진행률) 단계별로 1회씩 수집.
  // 데스크톱과 같은 기준이 되도록 헤더·푸터를 뺀 본문(Body)만 측정한다.
  useScrollDepth({
    contentRef,
    getScrollContainer: () => document.getElementById(SHEET_SCROLL_BODY_ID),
    enabled: isSheetExpanded && !!content?.length,
    resetKey: id,
    onReach: (percent) => {
      tracking('AI_analysis_scroll', {
        analysis_type: analysisType,
        area: areaName,
        percent,
      });
    },
  });

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const onVote = (vote: AiVoteStateType) => {
    tracking(`AI_analysis_reaction_${vote === 'up' ? 'like' : 'dislike'}`, {
      area: areaName,
    });
    setAiVoteList((prev) => ({...prev, [id]: vote}));
  };

  const copyAiPrompt = () => {
    const text = document.getElementById(CONTENT_ID)?.innerText;
    tracking('AI_analysis_contents_copy', {area: areaName});

    if (text && copyText(text)) {
      setCopied(true);
      toast.success('내용이 복사되었습니다');
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  // 로딩: 기초 분석 or AI 해설 생성 대기 → 데스크톱과 동일한 스켈레톤
  // 에러 시엔 로딩으로 빠지지 않도록 제외 (기초 분석 에러 시 data 없음 → AI 쿼리 disabled →
  // react-query v5에서 disabled 쿼리는 isPending=true 이므로 무한 스켈레톤 방지)
  if (!isError && (isPending || isAiPending)) {
    return (
      <Wrapper>
        <PanelHeader>
          <PanelTitle>상권 분석 AI 해설</PanelTitle>
          <Infomation />
        </PanelHeader>
        <AiSubHeader>
          <BlueInfoText>해당 지역 데이터를 읽고 있어요</BlueInfoText>
        </AiSubHeader>
        <SkeletonBody>
          <Lottie animationData={SkeltonLottie} />
        </SkeletonBody>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <PanelHeader>
        <PanelTitle>상권 분석 AI 해설</PanelTitle>
        <Infomation />
      </PanelHeader>

      <AiSubHeader
        isCopied={isCopied}
        onCopyClick={isError ? undefined : copyAiPrompt}
      >
        {isError ? (
          <BlueInfoText>오류가 발생했어요</BlueInfoText>
        ) : (
          !!dataUpdatedAt &&
          `${dayjs(dataUpdatedAt).format(FULL_DATE_FORMAT)} 기준`
        )}
      </AiSubHeader>

      <Body ref={contentRef}>
        <ContentWrapper>
          <AreaInfoBox>
            <TitleSection gap={6}>
              <AreaTitle ellipsis>{areaName}</AreaTitle>
              <AreaSub>
                {isError ? '분석 결과 다시 불러오기' : getAreaTitle(item)}
              </AreaSub>
            </TitleSection>
            {isError && (
              <ActionGuideTooltip
                content="다시 불러오기"
                place="top"
                offset={2}
              >
                <RefreshButton onClick={() => refetch()} />
              </ActionGuideTooltip>
            )}
          </AreaInfoBox>

          {!!content?.length && (
            <>
              <Note>
                상권 데이터 해설 가이드라인은 AI 모델에 의해 다양한 데이터를
                기반으로 생성되고 있어서 부정확할 수 있어요. 정확한 정보가
                전달될 수 있도록 최선을 다하고 있으나 투자 결정 시 제공된 정보에
                대한 이용 여부는 신중히 판단해 주세요.
              </Note>
              <Divider />
              <Content id={CONTENT_ID}>
                <Markdown>{content}</Markdown>
              </Content>
            </>
          )}
        </ContentWrapper>
      </Body>

      <FooterDivider />
      <FooterWrap>
        <PanelFooter voteState={voteState} onVote={onVote} />
      </FooterWrap>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* 하단 "지도 보기" 플로팅 버튼에 콘텐츠가 가려지지 않도록 여백 확보 */
  padding-bottom: 100px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 16px 4px;
`;

const PanelTitle = styled.div`
  color: ${palette.gray800};
  font-size: 16px;
  font-weight: 700;
`;

const BlueInfoText = styled.div`
  color: #2a76fc;
  font-weight: 500;
`;

const Body = styled.div`
  padding: 16px 16px 24px;
  font-weight: 400;
  line-height: 2;
  font-size: 14px;
  color: ${palette.grayBlack};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

/* 로딩 스켈레톤 전용: 패딩 없음 */
const SkeletonBody = styled.div`
  padding: 0;
`;

/* 데스크톱 AreaInfo와 동일한 스타일 */
const AreaInfoBox = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 12px 15px 12px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  background: ${palette.gray100};
  line-height: normal;
  gap: 16px;
`;

const TitleSection = styled(ColumnFlexBox)`
  flex-grow: 1;
  min-width: 0;
`;

const AreaTitle = styled(Typography)`
  color: ${palette.grayBlack};
  font-size: 14px;
  font-weight: 600;
  display: block;
`;

const AreaSub = styled.div`
  color: ${palette.gray600};
  font-size: 12px;
  font-weight: 400;
`;

const RefreshButton = styled(RefreshCircleIcon)`
  color: ${palette.teal100};
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  &:hover {
    color: ${palette.teal200};
  }
`;

const Note = styled.div`
  color: ${palette.gray600};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
`;

const Divider = styled.div`
  background-color: ${palette.gray200};
  height: 1px;
  width: 100%;
`;

const Content = styled.div`
  ul {
    padding-inline-start: 16px;
  }
`;

/* "이 내용이 도움이 되었나요?" 위 구분선: 좌우 4px 여백 (모바일 전용) */
const FooterDivider = styled.div`
  height: 1px;
  background-color: ${palette.gray200};
  margin: 0 14px;
`;

/* 재사용한 PanelFooter의 기본 border-top 제거 (위 FooterDivider로 대체) */
const FooterWrap = styled.div`
  > div {
    border-top: none;
    /* 데스크톱은 space-between이나, 모바일은 라벨 바로 옆에 좋아요/싫어요 아이콘을 붙임 */
    & > *:first-child {
      flex-grow: 0;
    }
  }
`;
